import React from 'react';
import { OTP_STEP } from './usePassengerOtpDialogue';
import { formatPhone, contactLabel } from '../../../utils/tripPaymentOtp';

/**
 * The passenger verification screens of the payment sheet (DRIVERAPP_OTP_SPEC.md
 * §"Visible flow"). Purely presentational — every decision lives in
 * usePassengerOtpDialogue, which both Live Dispatch and Itinerary share.
 *
 * The passenger needs no smartphone and no link: they read a texted code aloud and the
 * driver types it here. The hosted card page then opens on the DRIVER's device. That
 * is the whole point of this flow, so nothing here offers to send a payment link to
 * the passenger's phone as the way to pay.
 *
 * The code input is deliberately a text field with inputMode="numeric": a number input
 * would strip the leading zero from a code like "0123".
 */

const primaryBtn = 'w-full rounded-xl bg-blue-600 text-white font-semibold py-3 disabled:opacity-50';
const secondaryBtn = 'w-full rounded-xl border border-gray-200 text-gray-700 font-semibold py-3 disabled:opacity-50';

function Notice({ tone = 'info', children }) {
  const cls = tone === 'error'
    ? 'bg-red-50 border-red-200 text-red-800'
    : tone === 'good'
      ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
      : 'bg-blue-50 border-blue-200 text-blue-900';
  return (
    <p className={`text-sm rounded-lg border px-3 py-2 ${cls}`} role={tone === 'error' ? 'alert' : undefined}>
      {children}
    </p>
  );
}

function PassengerVerificationPanel({ dialogue, onCancel, tripLine }) {
  const {
    step, phone, setPhone, contacts, canCreate, selectedContactId, selectContact,
    newName, setNewName, code, setCode, contact, policy, busy, error, notice,
    codeComplete, lookup, sendCode, confirm, backToContacts,
  } = dialogue;

  const header = (
    <div className="text-center">
      <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500">
        Verify passenger
      </div>
      {tripLine && <div className="text-xs text-gray-500 mt-0.5">{tripLine}</div>}
    </div>
  );

  // ── Step 1: phone ─────────────────────────────────────────────────────────
  if (step === OTP_STEP.PHONE) {
    return (
      <div className="space-y-4">
        {header}
        <p className="text-sm text-gray-600">
          Enter the passenger's mobile number. They'll get a text with a code to read out
          to you — no app or smartphone needed.
        </p>
        <label className="block">
          <span className="text-xs font-semibold text-gray-500">Passenger phone</span>
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !busy) lookup(); }}
            placeholder="(555) 555-0100"
            className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-3 text-base"
            data-testid="otp-phone-input"
          />
        </label>
        {error && <Notice tone="error">{error}</Notice>}
        <button
          type="button"
          className={primaryBtn}
          disabled={busy || !phone.trim()}
          onClick={lookup}
          data-testid="otp-lookup"
        >
          {busy ? 'Looking up…' : 'Continue'}
        </button>
        <button type="button" className={secondaryBtn} onClick={onCancel} disabled={busy}>
          Back
        </button>
      </div>
    );
  }

  // ── Step 2: select or create the contact ──────────────────────────────────
  if (step === OTP_STEP.CONTACT) {
    // Selection is required even for a single match, and creation only when the
    // server allows it — we never invent a duplicate contact for a known number.
    const creating = canCreate && !selectedContactId;
    return (
      <div className="space-y-4">
        {header}
        <div className="text-sm text-gray-600">
          {contacts.length > 0
            ? 'Select the passenger for this number.'
            : 'No passenger is registered with this number yet.'}
          <span className="ml-1 font-semibold text-gray-800">{formatPhone(phone)}</span>
        </div>

        {contacts.length > 0 && (
          <div className="space-y-2" data-testid="otp-contact-list">
            {contacts.map((c) => {
              const selected = String(c.contactId) === String(selectedContactId);
              return (
                <button
                  key={c.contactId}
                  type="button"
                  onClick={() => selectContact(c.contactId)}
                  className={`w-full rounded-xl border px-4 py-3 text-left ${
                    selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'
                  }`}
                >
                  <span className="block font-semibold text-gray-900">{contactLabel(c)}</span>
                  <span className="block text-xs text-gray-500">{formatPhone(c.phone)}</span>
                </button>
              );
            })}
          </div>
        )}

        {canCreate && (
          <label className="block">
            <span className="text-xs font-semibold text-gray-500">Name (optional)</span>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Passenger name"
              className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-3 text-base"
              data-testid="otp-name-input"
            />
            <span className="mt-1 block text-xs text-gray-500">
              Leave blank to text the code without recording a name.
            </span>
          </label>
        )}

        {error && <Notice tone="error">{error}</Notice>}

        <button
          type="button"
          className={primaryBtn}
          disabled={busy || (!selectedContactId && !creating)}
          onClick={() => sendCode()}
          data-testid="otp-send"
        >
          {busy ? 'Sending…' : 'Send code'}
        </button>
        <button
          type="button"
          className={secondaryBtn}
          onClick={() => setPhone(phone)}
          disabled={busy}
        >
          Change number
        </button>
      </div>
    );
  }

  // ── Step 3: enter the texted code ─────────────────────────────────────────
  if (step === OTP_STEP.CODE) {
    return (
      <div className="space-y-4">
        {header}
        <p className="text-sm text-gray-600">
          Enter the code from the passenger's SMS.
          <span className="ml-1 font-semibold text-gray-800">
            {contact ? contactLabel(contact) : formatPhone(phone)}
          </span>
        </p>
        <label className="block">
          <span className="text-xs font-semibold text-gray-500">
            {policy.digits}-digit code
          </span>
          {/* text + numeric, never type="number": a leading zero must survive. */}
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            maxLength={policy.digits}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && codeComplete && !busy) confirm(); }}
            placeholder={'0'.repeat(policy.digits)}
            className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-3 text-center text-2xl font-semibold tracking-[0.4em]"
            data-testid="otp-code-input"
          />
        </label>
        {notice && <Notice tone="good">{notice}</Notice>}
        {error && <Notice tone="error">{error}</Notice>}
        <button
          type="button"
          className={primaryBtn}
          disabled={busy || !codeComplete}
          onClick={confirm}
          data-testid="otp-verify"
        >
          {busy ? 'Verifying…' : 'Verify'}
        </button>
        <button
          type="button"
          className={secondaryBtn}
          onClick={() => sendCode({ resend: true })}
          disabled={busy}
          data-testid="otp-resend"
        >
          Resend code
        </button>
        <button type="button" className={secondaryBtn} onClick={backToContacts} disabled={busy}>
          Back
        </button>
      </div>
    );
  }

  // ── Step 4: verified ──────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {header}
      <Notice tone="good">
        {contact ? contactLabel(contact) : 'The passenger'} is verified for this trip.
      </Notice>
      <p className="text-sm text-gray-600">Opening the secure card page…</p>
    </div>
  );
}

export default PassengerVerificationPanel;
